/**
 * Persist Cook browse filters + scroll target when opening recipe detail,
 * so "← Recipes" restores the prior browse context.
 */

import { getCookPreferencesEnabled } from './cookPreferencesSetting.js'

const SESSION_KEY = 'cook_browse_restore_v1'

/**
 * @typedef {object} CookBrowseFilters
 * @property {string} [searchTerm]
 * @property {boolean} [showFavoritesOnly]
 * @property {string[]} [selectedCuisines]
 * @property {string[]} [selectedHealthTiers]
 * @property {number|null} [selectedMaxTime]
 * @property {boolean} [prefFilterOn]
 * @property {boolean} [pantryMatchMode]
 * @property {string|null} [activePackFilter]
 * @property {'browse'|'add'} [getRecipesMethod]
 */

/**
 * @typedef {object} CookBrowseRestoreState
 * @property {CookBrowseFilters} filters
 * @property {string} scrollRecipeId
 * @property {number} [scrollTop]
 */

/** @returns {CookBrowseRestoreState|null} */
export function peekCookBrowseRestore() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (!parsed.scrollRecipeId || typeof parsed.scrollRecipeId !== 'string') return null
    if (!parsed.filters || typeof parsed.filters !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

/** @param {CookBrowseRestoreState} state */
export function saveCookBrowseRestore(state) {
  if (typeof sessionStorage === 'undefined' || !state?.scrollRecipeId) return
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
  } catch {
    // Ignore quota / private-mode errors.
  }
}

/** @returns {CookBrowseRestoreState|null} */
export function consumeCookBrowseRestore() {
  const state = peekCookBrowseRestore()
  clearCookBrowseRestore()
  return state
}

export function clearCookBrowseRestore() {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // Ignore.
  }
}

/** @param {CookBrowseFilters} [filters] */
export function normalizeCookBrowseFilters(filters = {}) {
  return {
    searchTerm: typeof filters.searchTerm === 'string' ? filters.searchTerm : '',
    showFavoritesOnly: Boolean(filters.showFavoritesOnly),
    selectedCuisines: Array.isArray(filters.selectedCuisines)
      ? filters.selectedCuisines.filter((c) => typeof c === 'string')
      : [],
    selectedHealthTiers: Array.isArray(filters.selectedHealthTiers)
      ? filters.selectedHealthTiers.filter((t) => typeof t === 'string')
      : [],
    selectedMaxTime:
      filters.selectedMaxTime == null || Number.isFinite(Number(filters.selectedMaxTime))
        ? (filters.selectedMaxTime == null ? null : Number(filters.selectedMaxTime))
        : null,
    prefFilterOn: Object.prototype.hasOwnProperty.call(filters, 'prefFilterOn')
      ? Boolean(filters.prefFilterOn)
      : getCookPreferencesEnabled(),
    pantryMatchMode: Boolean(filters.pantryMatchMode),
    activePackFilter:
      filters.activePackFilter == null ? null : String(filters.activePackFilter),
    getRecipesMethod: filters.getRecipesMethod === 'add' ? 'add' : 'browse',
  }
}
